import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { showToast } from './Toaster';
import type {
  ArtifactDto,
  ArtifactKind,
  ArtifactKindPolicyDto,
  ListArtifactKindPoliciesResponse,
  ListArtifactsResponse,
  ListRolloutReportsResponse,
  PlantOverviewClientDto,
  PlantOverviewDto,
  RolloutReportDto,
  RolloutTargetStatus,
  TriggerSingleClientRolloutRequest,
  TriggerSingleClientRolloutResponse,
} from '../api/types';
import { HeroRollout, RolloutProgress, IconCancel } from '../icons/HvacIcons';
import './hv-modal.css';
import '../pages/PlantsPage.css';

interface Props {
  plant: PlantOverviewDto;
  onClose(): void;
}

/**
 * Rollout trigger dialog reachable from a Plants overview row (added
 * 2026-08-29). Three-step flow:
 *  1) target picker — auto-selected when the plant has exactly one client,
 *     shown as a radio list when it has multiple. A plant with zero
 *     clients would not surface the button, but we defensively render an
 *     empty state anyway.
 *  2) artefact picker — /api/artifacts list, filtered client-side twice:
 *     to the kinds that make sense for the picked client's OS, and (J.16)
 *     to the kinds whose policy does not carry `hideFromRolloutUi`.
 *  3) reports view — same live-polling shape as the ArtifactsPage
 *     RolloutModal, kept structurally similar so the two dialogs behave
 *     the same for the operator.
 *
 * Contract: hits the same POST /api/artifacts/{id}/rollouts/single-client
 * endpoint that ArtifactsPage uses. Nothing is duplicated on the server
 * side. Report polling reuses /api/rollouts/{rolloutId}/reports.
 */
export function TriggerRolloutFromPlantsModal({ plant, onClose }: Props) {
  const [selectedClient, setSelectedClient] = useState<PlantOverviewClientDto | null>(
    plant.clients.length === 1 ? plant.clients[0] : null,
  );
  const [artifacts, setArtifacts] = useState<ArtifactDto[] | null>(null);
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  // J.16 braucht die Policy-Zeilen, um zu wissen, welcher Kind noch nicht
  // erprobt ist. Eigener State und kein Pflichtfeld: schlaegt der Request
  // fehl, filtert der Dialog nicht — die Serverseite lehnt trotzdem ab, was
  // sie ablehnen muss, und ein leerer Artefakt-Picker waere die schlechtere
  // Ausfallart.
  const [policies, setPolicies] = useState<ArtifactKindPolicyDto[] | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<ArtifactDto | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [rolloutId, setRolloutId] = useState<string | null>(null);
  const [reports, setReports] = useState<RolloutReportDto[]>([]);
  const pollHandle = useRef<number | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // Load artefacts once a client has been picked. We defer the fetch so
  // a Cancel-click before the picker resolves does not fire a stray HTTP
  // request.
  useEffect(() => {
    if (!selectedClient) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await api.get<ListArtifactsResponse>('/api/artifacts', ctrl.signal);
        setArtifacts(res.artifacts);
        try {
          const pol = await api.get<ListArtifactKindPoliciesResponse>(
            '/api/artifact-kind-policies', ctrl.signal,
          );
          setPolicies(pol.policies);
        } catch {
          // Siehe oben: ohne Policies wird nicht gefiltert.
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setArtifactsError(
          e instanceof ApiError
            ? `Artefakte konnten nicht geladen werden (${e.status}).`
            : 'Artefakte konnten nicht geladen werden.',
        );
      }
    })();
    return () => ctrl.abort();
  }, [selectedClient]);

  // Zwei Filter, aus zwei verschiedenen Gruenden.
  //
  // 1. OS: ein Artefakt, das die Plattform des Clients nicht anwenden kann,
  //    gehoert nicht in die Auswahl. Die Zuordnung ist absichtlich einfach;
  //    wer einen kind-fremden Rollout erzwingen will, kann das von der
  //    ArtifactsPage aus, die nicht einschraenkt.
  //
  //    `ClientLinuxArm64`/`ClientLinuxX64` fehlten hier — dieselbe Drift wie
  //    im Upload-Picker und in den vier Wire-Tabellen. Ein Client-Binary war
  //    damit aus diesem Dialog gar nicht ausrollbar, obwohl der Linux-Client
  //    einen Apply-Zweig dafuer hat.
  //
  // 2. J.16: was noch nicht erprobt ist, steht nicht klickbar da. Das ist ein
  //    Sicherheitsnetz gegen einen versehentlichen Klick, kein Rechtekonzept —
  //    per API bleibt der Kind triggerbar. Der Filter greift hier auf der
  //    Rollout-Erstellung, also genau dort, wo ein Fehlklick tatsaechlich ein
  //    System-Image auf ein Panel schiebt.
  const filteredArtifacts = useMemo(() => {
    if (!artifacts) return null;

    const hidden = new Set(
      (policies ?? []).filter((p) => p.hideFromRolloutUi).map((p) => p.kind),
    );
    const visible = artifacts.filter((a) => !hidden.has(a.kind));

    if (!selectedClient?.osKind) return visible;
    // Die Liste bildet ab, was der Client-Typ KANN — nicht, was wir uns zu
    // erlauben trauen. Die Frage "darf dieser Tenant das" haengt an
    // AllowTenantUpload und am Ops-Recht, nicht an einer stillen
    // Filterliste hier.
    //
    // config_file, cert_bundle und generic_blob sind dazugekommen: der
    // Panel-Agent wendet sie laut ArtifactApplier.kt an (`place(...)` in
    // CERTS_DIR / CONFIG_DIR / BLOBS_DIR). Dass ein Panel eine
    // Cert-Rotation ueber den Rollout-Weg bekommen darf, ist genau der
    // Zweck von S6; die Apply-Pfade existieren auf beiden Seiten.
    const androidKinds: ArtifactKind[] = [
      'AndroidApk', 'AndroidSystemOta', 'ConfigFile', 'CertBundle', 'GenericBlob',
    ];
    // ClientLinux* bleiben gelistet, obwohl der Apply sie derzeit mit
    // Begruendung verweigert (kein Rueckweg, siehe RolloutApplier
    // .RefuseClientBundle): die Verweigerung ist laut, sicher und erklaert
    // sich im Report. Sie hier auszublenden waere eine zweite Stelle, die
    // beim Landen des Rollback-Slice nachgezogen werden muesste — und eine
    // veraltete versteckte Liste ist genau der Fehler, um den es in diesem
    // ganzen Pfad geht.
    //
    // ClimatixUcf bleibt ebenfalls: der Apply ist ein Stub, der laut
    // scheitert, und ob der Kind hier stehen soll, entscheidet S7.
    const linuxKinds: ArtifactKind[] = [
      'ConfigFile', 'CertBundle', 'ClimatixUcf', 'GenericBlob',
      'ClientLinuxArm64', 'ClientLinuxX64',
    ];
    const allowed = selectedClient.osKind === 'android' ? androidKinds
      : selectedClient.osKind === 'linux' ? linuxKinds
      : null;
    if (!allowed) return visible;
    return visible.filter((a) => allowed.includes(a.kind));
  }, [artifacts, policies, selectedClient]);

  const trigger = async () => {
    if (!selectedClient || !selectedArtifact) return;
    setTriggering(true);
    try {
      const body: TriggerSingleClientRolloutRequest = { clientId: selectedClient.id };
      const res = await api.post<TriggerSingleClientRolloutResponse>(
        `/api/artifacts/${selectedArtifact.id}/rollouts/single-client`, body);
      setRolloutId(res.rolloutId);
      showToast({
        tone: 'success',
        title: 'Rollout ausgelöst',
        message: `RolloutId ${res.rolloutId.slice(0, 8)}...`,
        autoHideAfterMs: 3000,
      });
    } catch (err) {
      const msg = err instanceof ApiError
        ? String((err.body as { detail?: string; error?: string })?.detail
                  ?? (err.body as { error?: string })?.error
                  ?? err.message)
        : String(err);
      showToast({ tone: 'error', title: 'Rollout fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
    } finally {
      setTriggering(false);
    }
  };

  // Poll reports every 2 s once a rolloutId is set.
  useEffect(() => {
    if (!rolloutId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get<ListRolloutReportsResponse>(`/api/rollouts/${rolloutId}/reports`);
        if (cancelled) return;
        setReports(res.reports);
      } catch { /* keep old snapshot */ }
    };
    void tick();
    pollHandle.current = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollHandle.current !== null) {
        window.clearInterval(pollHandle.current);
        pollHandle.current = null;
      }
    };
  }, [rolloutId]);

  useEffect(() => {
    if (reports.length === 0) return;
    const allTerminal = reports.every((r) => isTerminal(r.status));
    if (allTerminal && pollHandle.current !== null) {
      window.clearInterval(pollHandle.current);
      pollHandle.current = null;
    }
  }, [reports]);

  const body = rolloutId ? (
    <ReportsView rolloutId={rolloutId} reports={reports} onClose={onClose} />
  ) : !selectedClient ? (
    <ClientPicker
      plant={plant}
      onPick={setSelectedClient}
    />
  ) : (
    <ArtifactPicker
      client={selectedClient}
      artifacts={filteredArtifacts}
      artifactsError={artifactsError}
      selectedArtifact={selectedArtifact}
      onSelect={setSelectedArtifact}
      triggering={triggering}
      onBack={plant.clients.length > 1 ? () => setSelectedClient(null) : undefined}
      onCancel={onClose}
      onTrigger={trigger}
    />
  );

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal hv-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Update auf "{plant.displayName}" auslösen</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__hero">
            <HeroRollout size={72} style={{ color: 'var(--accent)' }} />
          </div>
          {body}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// --- Client picker --------------------------------------------------------

function ClientPicker({
  plant,
  onPick,
}: {
  plant: PlantOverviewDto;
  onPick(client: PlantOverviewClientDto): void;
}) {
  if (plant.clients.length === 0) {
    return (
      <>
        <p class="hv-plants__empty">
          Diese Anlage hat noch keinen registrierten Client. Zum Ausrollen muss
          mindestens ein Feld-Client die Anlage per MQTT-Enrollment gemeldet
          haben.
        </p>
      </>
    );
  }
  return (
    <>
      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
        Ziel-Client wählen (die Anlage hat {plant.clients.length} registrierte
        Clients):
      </p>
      <div class="hv-plants__picker">
        {plant.clients.map((c) => (
          <button
            key={c.id}
            type="button"
            class="hv-plants__picker-item"
            onClick={() => onPick(c)}
          >
            <div class="hv-plants__picker-main">
              <span class="hv-plants__picker-name">
                {c.displayName ?? c.chassisDisplayName ?? c.mqttClientName}
              </span>
              <span class="hv-plants__picker-meta">
                {(c.osKind ?? 'unbekannt')} · {c.chassisDisplayName ?? c.hardwareKind ?? '?'} ·
                zuletzt gesehen {formatRelative(c.lastSeenAt)}
              </span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// --- Artefact picker + trigger --------------------------------------------

function ArtifactPicker({
  client,
  artifacts,
  artifactsError,
  selectedArtifact,
  onSelect,
  triggering,
  onBack,
  onCancel,
  onTrigger,
}: {
  client: PlantOverviewClientDto;
  artifacts: ArtifactDto[] | null;
  artifactsError: string | null;
  selectedArtifact: ArtifactDto | null;
  onSelect(a: ArtifactDto): void;
  triggering: boolean;
  onBack?: () => void;
  onCancel(): void;
  onTrigger(): void;
}) {
  return (
    <>
      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
        Ziel-Client: <strong>{client.displayName ?? client.chassisDisplayName ?? client.mqttClientName}</strong>
        {' '}({client.osKind ?? 'os?'})
      </p>
      {artifactsError && <p class="hv-modal__error">{artifactsError}</p>}
      {!artifacts && !artifactsError && <p class="hv-plants__empty">Lade Artefakte...</p>}
      {artifacts && artifacts.length === 0 && (
        <p class="hv-plants__empty">Keine passenden Artefakte im Tenant vorhanden.</p>
      )}
      {artifacts && artifacts.length > 0 && (
        <div class="hv-plants__artifact-list">
          {artifacts.map((a) => (
            <button
              key={a.id}
              type="button"
              class={`hv-plants__artifact-item ${selectedArtifact?.id === a.id ? 'hv-plants__artifact-item--selected' : ''}`}
              onClick={() => onSelect(a)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span class="hv-plants__artifact-name">{a.displayName || a.originalFileName}</span>
                <span class="hv-plants__artifact-meta">
                  {a.kind} · v{a.version} · sha256 {a.contentSha256.slice(0, 12)}...
                </span>
              </div>
              <span class="hv-plants__badge">{a.signatures.length}× signiert</span>
            </button>
          ))}
        </div>
      )}
      <div class="hv-modal__footer" style={{ padding: 'var(--space-3) 0 0', borderTop: 'none' }}>
        {onBack && <button type="button" class="hv-btn" onClick={onBack} disabled={triggering}>Zurück</button>}
        <div style={{ flex: 1 }} />
        <button type="button" class="hv-btn" onClick={onCancel} disabled={triggering}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
        </button>
        <button
          type="button"
          class="hv-btn hv-btn--primary"
          onClick={onTrigger}
          disabled={triggering || !selectedArtifact}
        >
          {triggering ? 'Sende...' : 'Rollout senden'}
        </button>
      </div>
    </>
  );
}

// --- Reports view ---------------------------------------------------------

function ReportsView({
  rolloutId,
  reports,
  onClose,
}: {
  rolloutId: string;
  reports: RolloutReportDto[];
  onClose(): void;
}) {
  const latest = reports[0];
  const allTerminal = reports.length > 0 && reports.every((r) => isTerminal(r.status));
  const anyRejected = reports.some(
    (r) => r.status === 'Failed' || r.status === 'RejectedSignatureInvalid',
  );
  const percent = reports.length === 0
    ? 5
    : Math.round((reports.filter((r) => isTerminal(r.status)).length / reports.length) * 100);

  return (
    <>
      <RolloutProgress value={percent} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-3)' }}>
        <div>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>RolloutId</div>
          <code class="hv-plants__mono" title={rolloutId}>{rolloutId}</code>
        </div>
        {latest && (
          <div style={{ marginLeft: 'auto' }}>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>Aktueller Stand</div>
            <span class={`hv-plants__badge hv-plants__badge--${statusTone(latest.status)}`}>{latest.status}</span>
          </div>
        )}
      </div>
      {allTerminal && !anyRejected && (
        <p style={{ marginTop: 'var(--space-3)', padding: 'var(--space-2)', color: 'var(--status-ok)' }}>
          Rollout abgeschlossen, alle Reports terminal.
        </p>
      )}
      {anyRejected && (
        <p class="hv-modal__error" style={{ marginTop: 'var(--space-3)' }}>
          Rollout enthält Fehlerreports. Details in der Tabelle.
        </p>
      )}
      <table class="hv-plants__reports">
        <thead>
          <tr>
            <th>Zeit</th>
            <th>Status</th>
            <th>Fehler</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id}>
              <td>{new Date(r.reportedAt).toLocaleString()}</td>
              <td><span class={`hv-plants__badge hv-plants__badge--${statusTone(r.status)}`}>{r.status}</span></td>
              <td class="hv-plants__mono">{r.errorMessage ?? ''}</td>
            </tr>
          ))}
          {reports.length === 0 && (
            <tr><td colSpan={3} class="hv-plants__empty">Warte auf Reports...</td></tr>
          )}
        </tbody>
      </table>
      <div class="hv-modal__footer" style={{ padding: 'var(--space-3) 0 0', borderTop: 'none' }}>
        <div style={{ flex: 1 }} />
        <button type="button" class="hv-btn hv-btn--primary" onClick={onClose}>Schließen</button>
      </div>
    </>
  );
}

function statusTone(status: RolloutTargetStatus): 'ok' | 'warn' | 'error' | 'info' {
  switch (status) {
    case 'Applied': return 'ok';
    case 'Failed': return 'error';
    case 'RejectedSignatureInvalid': return 'error';
    case 'Queued': return 'info';
    default: return 'warn';
  }
}

function isTerminal(status: RolloutTargetStatus): boolean {
  return status === 'Applied' || status === 'Failed' || status === 'RejectedSignatureInvalid';
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.round(hr / 24);
  return `${day} Tage`;
}
